import { type PortableTextBlock, stegaClean } from 'next-sanity'

import PortableText from '@/app/components/PortableText'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { getBottomSpacingClass, getTopSpacingClass } from '@/app/utils/sectionSpacing'
import {
  cn,
  ptGreen100H6,
  ptGreen500H6,
  ptWhiteTextHeaders,
  ptWhiteTextParagraphs,
} from '@/app/utils/style'
import { ContentSection as ContentSectionProps } from '@/sanity.types'

type ContentProps = {
  block: ContentSectionProps & { _key?: string }
  index: number
  pageId?: string
  pageType?: string
}

export type SectionStyleConfig = {
  container: string
  inner: string
  portableText?: string | string[]
}

export type SectionStyle = 'default' | 'lightGray' | 'green'

export const sectionStyleClasses: Record<SectionStyle, SectionStyleConfig> = {
  default: {
    container: 'content-container',
    inner: '',
    portableText: ptGreen500H6,
  },
  lightGray: {
    container: 'content-container-dark bg-neutral-50 py-8xl lg:py-12xl',
    inner: '',
    portableText: '',
  },
  green: {
    container: 'content-container-dark bg-green-800 py-8xl lg:py-12xl',
    inner: '',
    portableText: [ptWhiteTextHeaders, ptWhiteTextParagraphs, ptGreen100H6],
  },
}

export default function ContentSection({ block, pageId, pageType }: ContentProps) {
  if (!block?.content?.length) {
    return null
  }

  const style = stegaClean(block.style) || 'default'
  const styleConfig = sectionStyleClasses[style as SectionStyle] || sectionStyleClasses.default

  const topSpacingClass = getTopSpacingClass(stegaClean(block.topSpacing))
  const bottomSpacingClass = getBottomSpacingClass(stegaClean(block.bottomSpacing))

  return (
    <div className={cn(styleConfig.container)}>
      <div
        className={cn(
          'max-w-[1144px] mx-auto break-words',
          styleConfig.inner,
          topSpacingClass,
          bottomSpacingClass,
        )}
      >
        <PortableText
          className={cn(styleConfig.portableText)}
          paragraphClassName="[&_a]:text-green-500 [&_a]:break-all"
          listsClassName="[&_a]:text-green-500 [&_a]:break-all"
          value={block.content as PortableTextBlock[]}
          tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
          documentId={pageId}
          documentType={pageType}
          parentPath={block._key ? `pageBuilder[_key=="${block._key}"].content` : undefined}
          headingsAsParagraphs={stegaClean(block.headingsAsParagraphs) ?? false}
        />
      </div>
    </div>
  )
}
